import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import ImageWithLoader from "../Commaon/ImageWithLoader";
import { formatDateTime } from "../../utils/date";
import SuggestionForm from "./SuggestionForm";
import {
  ADMIN_STATUSES,
  ActivityTimeline,
  StatusBadge,
  canDeleteFiles,
  deleteFilesBlockedReason,
  getFiles,
  isCancelled,
} from "./prescriptionUtils";

const Field = ({ label, children }) => (
  <div>
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold text-break">{children || "-"}</div>
  </div>
);

const Section = ({ title, right, children }) => (
  <div className="border rounded-3 p-3">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h6 className="mb-0">{title}</h6>
      {right}
    </div>
    {children}
  </div>
);

/**
 * Detail view for one prescription: user/member, files, status, internal note,
 * suggestion form and activity timeline. The parent owns the prescription object;
 * every successful change is reported through `onUpdated(updatedPrescription)`.
 */
const PrescriptionDetailModal = ({ prescription, onHide, onUpdated, onDeleteFiles, notify }) => {
  const [saving, setSaving] = useState(false);
  const [noteValue, setNoteValue] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);

  const id = prescription?._id;
  const savedNote = prescription?.adminNote || "";

  // Load the latest copy when the modal opens for a prescription.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      const response = await AuthInterceptor(`/admin/prescriptions/${id}`, { method: "GET" });
      if (!cancelled && response?.data?.s === 200 && response.data.r) onUpdated(response.data.r);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setNoteValue(savedNote);
  }, [id, savedNote]);

  useEffect(() => {
    setPreviewIndex(0);
  }, [id]);

  const request = async (url, method, body, fallbackError) => {
    setSaving(true);
    try {
      const response = await AuthInterceptor(url, { method, body: JSON.stringify(body) });
      if (response?.data?.s === 200 && response.data.r) {
        onUpdated(response.data.r);
        notify(response.data.m || "Saved", "success");
        return true;
      }
      notify(response?.data?.m || fallbackError, "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!prescription) return null;

  const cancelled = isCancelled(prescription);
  const files = getFiles(prescription);
  const previewFile = files[previewIndex] || files[0];
  const user = prescription.userDetails || {};
  const member = prescription.member;
  const deletable = canDeleteFiles(prescription);

  return (
    <Modal show onHide={onHide} centered size="xl" scrollable dialogClassName="prescription-detail-modal">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          Prescription Details <StatusBadge status={prescription.status} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        <div className="row g-3">
          {/* Left: who, files, timeline */}
          <div className="col-lg-7 d-grid gap-3 align-content-start prescription-detail-col">
            <Section title="User & member">
              <div className="row g-3">
                <div className="col-sm-6">
                  <Field label="User">{user.fullName}</Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Mobile">{user.mobileNumber || prescription.mobileNumber}</Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Member">
                    {member ? (
                      <>
                        {member.fullName || "-"}
                        {member.relation && <span className="text-muted fw-normal"> ({member.relation})</span>}
                      </>
                    ) : null}
                  </Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Gender / Age">
                    {member ? [member.gender, member.age ? `${member.age} yrs` : ""].filter(Boolean).join(" · ") : null}
                  </Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Uploaded">{formatDateTime(prescription.createdAt)}</Field>
                </div>
                <div className="col-sm-6">
                  <Field label="Email">{user.email}</Field>
                </div>
              </div>
            </Section>

            <Section
              title={`Files${files.length ? ` (${files.length})` : ""}`}
              right={
                <span title={deletable ? "" : deleteFilesBlockedReason(prescription)}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={!deletable || saving}
                    onClick={() => onDeleteFiles(prescription)}
                  >
                    Delete files
                  </button>
                </span>
              }
            >
              {prescription.filesDeletedAt ? (
                <div className="text-muted small">Files removed on {formatDateTime(prescription.filesDeletedAt)}</div>
              ) : !files.length ? (
                <div className="text-muted small">No files</div>
              ) : (
                <>
                  {previewFile && previewFile.type !== "pdf" && (
                    <div className="text-center bg-white border rounded mb-2 p-2">
                      <a href={previewFile.url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                        <ImageWithLoader
                          src={previewFile.url}
                          alt={previewFile.name || "Prescription"}
                          style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }}
                        />
                      </a>
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    {files.map((file, index) =>
                      file.type === "pdf" ? (
                        <a
                          key={`${file.url}-${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex flex-column align-items-center justify-content-center border rounded bg-white text-danger text-decoration-none"
                          style={{ width: "72px", height: "72px" }}
                          title={`${file.name || "PDF"} (opens in new tab)`}
                        >
                          <span className="fw-bold">PDF</span>
                          <span className="small text-muted text-truncate px-1" style={{ maxWidth: "70px" }}>
                            {file.name || `File ${index + 1}`}
                          </span>
                        </a>
                      ) : (
                        <button
                          key={`${file.url}-${index}`}
                          type="button"
                          className={`p-0 border rounded bg-white ${
                            previewFile === file ? "border-primary border-2" : ""
                          }`}
                          style={{ width: "72px", height: "72px", overflow: "hidden" }}
                          onClick={() => setPreviewIndex(index)}
                          title={file.name || `Image ${index + 1}`}
                        >
                          <ImageWithLoader
                            src={file.url}
                            alt={file.name || `Image ${index + 1}`}
                            style={{ width: "70px", height: "70px", objectFit: "cover" }}
                          />
                        </button>
                      )
                    )}
                  </div>
                </>
              )}
            </Section>

            <Section title="Activity">
              <ActivityTimeline activity={prescription.activity} />
            </Section>
          </div>

          {/* Right: work the prescription */}
          <div className="col-lg-5 d-grid gap-3 align-content-start prescription-detail-col">
            <Section title="Status">
              {cancelled ? (
                <div className="small text-muted">
                  <StatusBadge status="Cancelled" /> Cancelled by the user. Status can no longer be changed.
                </div>
              ) : (
                <select
                  className="form-select form-select-sm"
                  value={prescription.status || "Open"}
                  disabled={saving}
                  onChange={(event) =>
                    request(
                      `/admin/prescriptions/${id}`,
                      "PUT",
                      { status: event.target.value },
                      "Error updating status"
                    )
                  }
                  aria-label="Change status"
                >
                  {ADMIN_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              )}
            </Section>

            <Section title="Internal note (not shown to user)">
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={noteValue}
                onChange={(event) => setNoteValue(event.target.value)}
                disabled={saving}
                placeholder="Only visible to admins"
              />
              <div className="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={saving || noteValue === savedNote}
                  onClick={() =>
                    request(`/admin/prescriptions/${id}`, "PUT", { adminNote: noteValue }, "Error saving note")
                  }
                >
                  Save note
                </button>
              </div>
            </Section>

            <Section
              title="Suggestion for the user"
              right={
                prescription.suggestion?.sentAt && (
                  <span className="text-muted small">Sent {formatDateTime(prescription.suggestion.sentAt)}</span>
                )
              }
            >
              {cancelled && (
                <div className="alert alert-secondary py-1 px-2 small mb-2">
                  Suggestions can't be sent for a cancelled prescription.
                </div>
              )}
              {!cancelled && prescription.suggestion && (
                <div className="text-muted small mb-2">
                  Editing replaces the current suggestion and notifies the user again.
                </div>
              )}
              {!cancelled && !prescription.suggestion && prescription.status === "Open" && (
                <div className="text-muted small mb-2">Sending a suggestion moves this prescription to Processing.</div>
              )}
              <SuggestionForm
                key={`${id}-${prescription.suggestion?.sentAt || "new"}`}
                suggestion={prescription.suggestion}
                disabled={cancelled}
                saving={saving}
                onSubmit={(body) =>
                  request(`/admin/prescriptions/${id}/suggestion`, "PUT", body, "Error sending suggestion")
                }
              />
            </Section>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PrescriptionDetailModal;
